import { createContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import quoteActions from "actions/quote";
import orderActions from "actions/order";
import invoiceActions from "actions/invoice";
import subscriptionActions from "actions/subscription";
import accountActions from "actions/account";
import productCategoryActions from "actions/product_category";

export const ACTIONS = {
    UPSELL: 'upsell',
    CONVERT_NEW_CUSTOMER: 'convert_to_new_customer',
    DEMO: 'demo'
}

export const CURRENT_STEPS = {
    BUILD_QUOTE: "build-quote",
    ADD_PRODUCTS: "add-products",
    PROPOSAL_PREVIEW: "proposal-preview",
    ORDER: "order",
    EDIT_ORDER: "edit-order",
    CHECKOUT: "checkout",
    INVOICE: "invoice"
}

export const QuoteBuilderContext = createContext();

const QuoteBuilderProvider = ({ children, refreshAccountInformation }) => {
    const dispatch = useDispatch();
    const isDyl = useSelector(state => state.auth.dyl_employee);

    const [quoteBuilderConfig, setQuoteBuilderConfig] = useState({
        currentStep: null,
        id: null,
    });

    const calculateTaxes = (cart, account_id, order_id)  => {
        if (isDyl) {
            const pricing_schedule = cart.filter(item => item.pricing_schedule && item.dyl_product_name !== 'sms_balance')[0]?.pricing_schedule;
            dispatch(orderActions.calculateTaxes({
                price_schedule: pricing_schedule || undefined,
                products: cart.map(item => ({
                    product_id: item.product_id,
                    quantity: item.quantity,
                    amount: item.dyl_product_name !== 'sms_balance' ? undefined : Number(item.item_price || 0),
                    discount: Number(item.item_discount?.discount || 0)
                }))
            }, { account_id }, order_id));
        }
    }

    const onOpenAddProducts = (config) => {
        const { quote_id = null, isUpsell = false, order_id = null, action } = config || {};
        dispatch(productCategoryActions.readProductCategories());
        setQuoteBuilderConfig({
            currentStep: CURRENT_STEPS.ADD_PRODUCTS,
            id: quote_id || order_id || null,
            isUpsell,
            action
        });
    };

    const onRefreshAccountInformation = () => {
        if (refreshAccountInformation) {
            return refreshAccountInformation();
        }
        return null;
    };

    const onEditQuote = (quote_id, action) => {
        if (quote_id) {
            dispatch(quoteActions.readQuote(quote_id));
        }
        setQuoteBuilderConfig({
            currentStep: quote_id ? CURRENT_STEPS.BUILD_QUOTE : CURRENT_STEPS.ADD_PRODUCTS,
            id: quote_id || null,
            action
        });
    };

    const onViewProposal = (version_id) => {
        dispatch(quoteActions.getVersion(version_id));
        setQuoteBuilderConfig({
            currentStep: CURRENT_STEPS.PROPOSAL_PREVIEW,
            id: version_id,
        });
    };

    const onViewOrder = (order_id, account_id) => {
        dispatch(accountActions.readContactsForPinning(account_id));
        if (order_id) {
            dispatch(orderActions.readOne(account_id, null, order_id)).then(order => {
                if (order?.order_status === "draft" || order?.order_status === "open") {
                    dispatch(subscriptionActions.getProducts(account_id));
                    dispatch(subscriptionActions.getNextInvoice(account_id));
                    calculateTaxes(order?.order_items || [], account_id, order_id);
                }
            });
        }
        setQuoteBuilderConfig({
            ...(quoteBuilderConfig?.action === ACTIONS.DEMO ? { action: ACTIONS.DEMO } : {}),
            currentStep: CURRENT_STEPS.ORDER,
            id: order_id,
        });
    };

    const onEditOrder = (order_id, account_id) => {
        dispatch(accountActions.readContactsForPinning(account_id));
        dispatch(orderActions.readOne(account_id, null, order_id)).then(order => {
            if (order?.order_status === "draft" || order?.order_status === "open") {
                dispatch(subscriptionActions.getProducts(account_id));
                dispatch(subscriptionActions.getNextInvoice(account_id));
                calculateTaxes(order?.order_items || [], account_id, order_id);
            }
        });
        setQuoteBuilderConfig({
            currentStep: CURRENT_STEPS.EDIT_ORDER,
            id: order_id
        });
    };

    const onCheckout = () => {
        setQuoteBuilderConfig({
            ...quoteBuilderConfig,
            currentStep: CURRENT_STEPS.CHECKOUT,
        });
    };

    const onViewInvoice = (invoice_id, isAdmin) => {
        dispatch(invoiceActions.readOne(invoice_id, isAdmin ? { isAdmin } : {}));
        setQuoteBuilderConfig({
            currentStep: CURRENT_STEPS.INVOICE,
            id: invoice_id
        })
    }

    const onCloseModal = () => {
        setQuoteBuilderConfig({ currentStep: null, id: null });
    };

    return (
        <QuoteBuilderContext.Provider
            value={{
                quoteBuilderConfig,
                onOpenAddProducts,
                onEditQuote,
                onViewProposal,
                onCloseModal,
                onViewOrder,
                onCheckout,
                onRefreshAccountInformation,
                onEditOrder,
                onViewInvoice
            }}
        >
            {children}
        </QuoteBuilderContext.Provider>
    );
};

export default QuoteBuilderProvider;
